import * as React from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query"
import { useForm } from "react-hook-form"
import * as z from "zod"

import { PositionType } from "@/types/billing"
import { addPosition, getDepartmentDropdown } from "@/lib/api/billing"
import { useToast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import { DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Header } from "@/components/header"

import { ComboboxSearch } from "../combobox-search"
import { Icons } from "../icons"
import { Card } from "../ui/card"
import { Input } from "../ui/input"

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  department_id: z.coerce.number().min(1, "Department is required"),
})

export default function PositionForm({
  setShowAddPositionPage,
}: {
  setShowAddPositionPage: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      department_id: 0,
    },
  })

  const queryConfigs = [
    {
      queryKey: ["departments"],
      queryFn: getDepartmentDropdown,
    },
  ]

  const results = useQueries({
    queries: queryConfigs,
  })

  const [departments] = results.map((result) => result.data || [])

  console.log(departments)

  const mutation = useMutation({
    mutationFn: async (data: PositionType) => {
      await addPosition(data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["positionDetails"] })
      toast({
        description: "New Position added successfully.",
      })
      setShowAddPositionPage(false)
    },
    onError: async (error: any) => {
      const response = error.response
      let errorMessage = "Please try again."
      console.log(error)

      if (response && response.status === 400) {
        const errorData = await response.json()
        console.error("API Error Response:", errorData)
        errorMessage = errorData.message || JSON.stringify(errorData)
      }

      toast({
        title: "Something went wrong!",
        description: errorMessage,
        variant: "destructive",
      })
    },
  })

  function handleSubmit(values: z.infer<typeof formSchema>) {
    mutation.mutate(values as PositionType)
  }

  return (
    <div className="px-2">
      <Header
        subroutes={[
          {
            route: "/user-role-management/position",
            label: "Position",
          },
        ]}
      >
        Add Position
      </Header>
      <DropdownMenuSeparator className="mt-4" />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="grid grid-cols-1 gap-4 lg:container md:max-w-[1000px]"
        >
            <Card className="my-4 p-6">
            <FormField
              control={form.control}
              name="department_id"
              render={({ field }) => (
                <FormItem className="col-span-1 flex flex-col gap-1 space-y-1.5">
                  <FormLabel>
                    Department<span className="text-red-600">*</span>
                  </FormLabel>
                  <ComboboxSearch
                    field={field}
                    options={departments}
                    placeholder="Select Department"
                    idAsValue
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Name<span className="text-red-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Card>
          <div className="flex flex-row justify-end gap-2 md:mr-4">
            <Button
              variant="outline"
              type="button"
              onClick={() => {
                setShowAddPositionPage(false)
              }}
              className="w-28"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending}
              className="w-28"
            >
              {mutation.isPending && (
                <Icons.loader className="mr-2 animate-spin" />
              )}
              Add
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
